'use client';

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  Component,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { useLocale } from '@hooks/useLocale';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import { CaretDownIcon, XIcon } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'motion/react';
import { usePromoBarRotation } from '@hooks/usePromoBarRotation';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';
import StoreSelector from './StoreSelector/index';
import LinkButton from '_components/UI/LinkButton';
import SimpleLink from '_components/UI/SimpleLink';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import {
  PromoBarDocumentData,
  PromoBarDocumentDataMessagesItem,
} from 'prismicio-types';
import Container from '@components/global/Container';
import OutsideClickHandler from 'react-outside-click-handler';
import IconButton from '_components/UI/IconButton';
import {
  PromoBarLevels,
  trackPromoBarClick,
} from '_utils/GA4/trackPromoBarEvents';
import { KeyTextField } from '@prismicio/client';
import { MENU_PATH_DELIMITER } from '_utils/GA4/trackMegaMenuEvents';

import styles from './PromoBar.module.scss';

export type TranslateFn = (
  key: string,
  values?: Record<string, unknown>
) => string;

export interface PromoBarProps {
  alternateLocaleUrl?: string;
  promoBarData?: PromoBarDocumentData['messages'];
  /** When provided (e.g. from error boundary fallback), used instead of useTranslations. Enables rendering when next-intl is unavailable. */
  t?: TranslateFn;
}

function PromoBarInner({
  alternateLocaleUrl,
  promoBarData,
  t,
}: PromoBarProps & { t: TranslateFn }) {
  const locale = useLocale();
  const [levelOneHeading, setLevelOneHeading] = useState<KeyTextField | string>(
    ''
  );
  const [open, setOpen] = useState(false);
  const promoBarRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const scrollLockTargetRef = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLevelOneButtonClick = (heading: KeyTextField | string) => {
    if (open) {
      handleClose();
    } else {
      setLevelOneHeading(heading);
      trackPromoBarClick({
        data: {
          level: PromoBarLevels.level_1,
          linkUrl: null,
          linkText: heading,
          menuPath: heading,
        },
      });
      setOpen(true);
    }
  };

  const handleLinkClick = (
    level: PromoBarLevels,
    heading: KeyTextField | string,
    linkUrl?: KeyTextField | string
  ) => {
    trackPromoBarClick({
      data: {
        level,
        linkUrl: linkUrl ?? null,
        linkText: heading,
        menuPath: levelOneHeading
          ? `${levelOneHeading}${MENU_PATH_DELIMITER}${heading}`
          : heading,
      },
    });
    if (linkUrl) {
      window.location.href = linkUrl;
    }
  };

  const { currentIndex, fadeDuration } = usePromoBarRotation({
    itemCount: promoBarData?.length ?? 0,
    refreshTime: 6000,
    fadeTime: 500,
  });

  // Motion variants for fade animations
  const fadeVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const fadeTransition = {
    duration: fadeDuration / 1000,
    ease: 'easeInOut' as const,
  };

  // Motion variants for expanded promo bar slide animations
  const slideVariants = {
    initial: { y: '-100%' },
    animate: { y: 0 },
    exit: { y: '-100%' },
  };

  const slideTransition = {
    duration: 0.6,
    ease: [0.4, 0, 0.2, 1] as const,
  };

  useEffect(() => {
    if (open && closeButtonRef.current) {
      const timeoutId = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const target = expandedRef.current;
      if (target) {
        scrollLockTargetRef.current = target;
        disableBodyScroll(target);
      }
    } else {
      const target = scrollLockTargetRef.current;
      if (target) {
        enableBodyScroll(target);
        scrollLockTargetRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, handleClose]);

  return (
    <section className={styles.promoBarSection} ref={promoBarRef}>
      <a href="#main-content" className={styles.skipLink}>
        {t('skip.to.content')}
      </a>
      <a href="#navigation" className={styles.skipLink}>
        {t('skip.to.navigation')}
      </a>
      <div className={styles.promoBar}>
        <div className={styles.startContent}>
          <AnimatePresence mode="wait" initial={false}>
            {promoBarData?.map(
              (message: PromoBarDocumentDataMessagesItem, i: number) => {
                if (currentIndex !== i) return null;

                return (
                  <motion.div
                    key={`${message.heading}-promo-bar-${currentIndex}`}
                    className={styles.messageItem}
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={fadeTransition}
                  >
                    <button
                      type="button"
                      className={styles.messageButton}
                      onClick={() => handleLevelOneButtonClick(message.heading)}
                      aria-expanded={open}
                      aria-controls="expanded-promo-bar"
                      aria-label={t('promoBar.expandDetails', {
                        message: message.heading,
                      })}
                    >
                      {(() => {
                        const heading = message.heading ?? '';
                        const words = heading
                          .trim()
                          .split(/\s+/)
                          .filter(Boolean);
                        if (words.length <= 1) {
                          return (
                            <span className={styles.keepWithIcon}>
                              {heading}{' '}
                              <CaretDownIcon
                                width={16}
                                height={16}
                                className={styles.messageButtonIcon}
                              />
                            </span>
                          );
                        }
                        const lastWord = words.pop()!;
                        const rest = words.join(' ');
                        return (
                          <>
                            {rest && <span>{rest} </span>}
                            <span className={styles.keepWithIcon}>
                              {lastWord}
                              <CaretDownIcon
                                width={16}
                                height={16}
                                className={styles.messageButtonIcon}
                              />
                            </span>
                          </>
                        );
                      })()}
                    </button>
                  </motion.div>
                );
              }
            )}
          </AnimatePresence>
        </div>
        <div className={styles.endContent}>
          <StoreSelector isPromoBar />
          <div className={styles.endContentDesktop}>
            <SimpleLink
              mode="dark"
              size="small"
              href={`/${locale ?? 'en'}/explore/learn`}
              onClick={() =>
                handleLinkClick(
                  PromoBarLevels.level_1,
                  t('promoBar.expertAdvice'),
                  `/${locale}/explore/learn`
                )
              }
            >
              {t('promoBar.expertAdvice')}
            </SimpleLink>
            <SimpleLink
              mode="dark"
              size="small"
              href={`/${locale ?? 'en'}/explore/support`}
              onClick={() =>
                handleLinkClick(
                  PromoBarLevels.level_1,
                  t('promoBar.help'),
                  `/${locale}/explore/support`
                )
              }
            >
              {t('promoBar.help')}
            </SimpleLink>
            <SimpleLink
              mode="dark"
              size="small"
              href={alternateLocaleUrl ?? `/${locale === 'en' ? 'fr' : 'en'}`}
              onClick={() =>
                handleLinkClick(
                  PromoBarLevels.level_1,
                  t('alternate.locale.link'),
                  alternateLocaleUrl ?? `/${locale === 'en' ? 'fr' : 'en'}`
                )
              }
            >
              {t('alternate.locale.link')}
            </SimpleLink>
          </div>
        </div>
        {typeof document !== 'undefined' &&
          createPortal(
            <AnimatePresence>
              {open && (
                <>
                  <motion.div
                    key="promo-overlay"
                    className={styles.expandedPromoBarOverlay}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={handleClose}
                    aria-hidden="true"
                  />
                  <OutsideClickHandler onOutsideClick={handleClose}>
                    <motion.div
                      key="promo-dialog"
                      ref={expandedRef}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="promo-bar-title"
                      id="expanded-promo-bar"
                      className={styles.expandedPromoBarContainer}
                      variants={slideVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      transition={slideTransition}
                    >
                      <div className={styles.expandedPromoBarHeader}>
                        <h2
                          id="promo-bar-title"
                          className={styles.visuallyHidden}
                        >
                          {levelOneHeading}
                        </h2>
                        <div className={styles.closeIconButtonMobile}>
                          <IconButton
                            icon={XIcon}
                            size="sm"
                            shape="circular"
                            type="transparent"
                            iconSize={24}
                            onClick={handleClose}
                            aria-label={t('promoBar.close')}
                            className={styles.closeIconButtonMobile}
                          />
                        </div>
                        <div className={styles.closeIconButtonDesktop}>
                          <IconButton
                            icon={XIcon}
                            type="outlineDark"
                            size="xl"
                            iconSize={16}
                            shape="circular"
                            onClick={handleClose}
                            aria-label={t('promoBar.close')}
                            className={styles.closeIconButtonDesktop}
                          />
                        </div>
                      </div>
                      <motion.div
                        className={styles.contentContainer}
                        initial="hidden"
                        animate="visible"
                        variants={{
                          hidden: {},
                          visible: {
                            transition: {
                              staggerChildren: 0.08,
                              delayChildren: 0.1,
                            },
                          },
                        }}
                      >
                        {promoBarData?.map(
                          (
                            message: PromoBarDocumentDataMessagesItem,
                            index: number
                          ) => (
                            <React.Fragment
                              key={`${message.heading}-expanded-${index}`}
                            >
                              <motion.div
                                key={`${message.heading}-expanded-promo-bar`}
                                className={styles.expandedPromoBarItem}
                                variants={{
                                  hidden: { y: -24 },
                                  visible: {
                                    y: 0,
                                    transition: {
                                      duration: 0.34,
                                      ease: [0.22, 1, 0.36, 1],
                                    },
                                  },
                                }}
                              >
                                <div
                                  className={styles.expandedPromoBarItemTitle}
                                >
                                  {message.heading}
                                </div>
                                <div
                                  className={styles.expandedPromoBarItemMessage}
                                >
                                  {message.body}
                                </div>
                                {message.cta_link && (
                                  <LinkButton
                                    href={message.cta_link}
                                    onClick={() =>
                                      handleLinkClick(
                                        PromoBarLevels.level_2,
                                        message.cta_text,
                                        message.cta_link
                                      )
                                    }
                                    variant="white"
                                    className={styles.expandedPromoBarItemLink}
                                  >
                                    {message.cta_text}
                                  </LinkButton>
                                )}
                              </motion.div>
                              <motion.div
                                key={`${message.heading}-expanded-promo-bar-divider`}
                                className={cn(
                                  styles.expandedPromoBarItemDivider,
                                  {
                                    [styles.expandedPromoBarItemDividerLast]:
                                      index === promoBarData?.length - 1,
                                  }
                                )}
                                variants={{
                                  hidden: { y: -16 },
                                  visible: {
                                    y: 0,
                                    transition: {
                                      duration: 0.26,
                                      ease: [0.22, 1, 0.36, 1],
                                    },
                                  },
                                }}
                              />
                            </React.Fragment>
                          )
                        )}
                      </motion.div>
                    </motion.div>
                  </OutsideClickHandler>
                </>
              )}
            </AnimatePresence>,
            document.body
          )}
      </div>
    </section>
  );
}

function PromoBarWithTranslations(props: PromoBarProps) {
  const t = useTranslationWithSelfFallback('common');
  return <PromoBarInner {...props} t={t as TranslateFn} />;
}

const fallbackT: TranslateFn = (key) => key;

class PromoBarErrorBoundary extends Component<
  { children: ReactNode } & PromoBarProps,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      const { children: omittedChildren, ...rest } = this.props;
      return <PromoBarInner {...rest} t={fallbackT} />;
    }
    return this.props.children;
  }
}

function PromoBar(props: PromoBarProps) {
  const hasMounted = useHasMounted();
  return (
    <PromoBarErrorBoundary {...props}>
      {hasMounted ? (
        <PromoBarWithTranslations {...props} />
      ) : (
        <PromoBarInner {...props} t={fallbackT} />
      )}
    </PromoBarErrorBoundary>
  );
}

export default PromoBar;
